import type { AlbumListItem } from "../types/album.types.ts"
import type { AlbumSortOrder } from "../types/album-sort.types.ts"

export const sortAlbums = (
  albums: AlbumListItem[],
  order: AlbumSortOrder
): AlbumListItem[] => {
  const sorted = [...albums]
  switch (order) {
    case "created_desc":
      return sorted.sort((a, b) => (a.created < b.created ? 1 : -1))
    case "name_asc":
      return sorted.sort((a, b) => a.name.localeCompare(b.name))
    case "name_desc":
      return sorted.sort((a, b) => b.name.localeCompare(a.name))
    case "files_desc":
      return sorted.sort((a, b) => b.attachments_count - a.attachments_count)
    case "files_asc":
      return sorted.sort((a, b) => a.attachments_count - b.attachments_count)
    case "modified_desc":
    default:
      return sorted.sort((a, b) => (a.updated < b.updated ? 1 : -1))
  }
}

/** The server's `ordering` value for a sort the API can do itself.
 *
 * The date orders have to be server-side: the list is paginated, so sorting
 * what arrived orders a page rather than the album set, and the API renders
 * datetimes in the viewer's own date format — `09/07/2026` and `Sep 07, 2026`
 * do not compare chronologically as strings.
 *
 * File-count returns null: `attachments_count` is computed from the
 * permission-scoped attachments a viewer may see, so the API cannot order by
 * it without annotating that same scoping. Those two stay client-side, and so
 * stay page-scoped, until it can.
 */
export const albumSortToOrdering = (order: AlbumSortOrder): string | null => {
  switch (order) {
    case "modified_desc":
      return "-updated"
    case "created_desc":
      return "-created"
    case "name_asc":
      return "name"
    case "name_desc":
      return "-name"
    default:
      return null
  }
}

/** Only the orders the server cannot do are still applied here. */
export const sortAlbumsClientSide = (
  albums: AlbumListItem[],
  order: AlbumSortOrder
): AlbumListItem[] =>
  albumSortToOrdering(order) === null ? sortAlbums(albums, order) : albums
